import { trpc } from "../trpc";
import { useMemo, useRef } from "react";
import {
  ConnectedAsset,
  transformCanvasConnections,
  transformChatbotConnections,
  transformInterfaceConnections,
  transformSubscriptionConnections,
  transformTableViewConnections,
  transformZapConnections,
} from "./tableConnectedAssetTransforms";

export type { ConnectedAsset };

/**
 * Returns the same array reference across renders as long as its elements
 * are shallowly equal, even when its length changes. A plain variable-length
 * array can't be passed as a useMemo dependency list -- React only compares
 * indices up to the shorter of the two lengths, so growing from `[]` reads
 * as "unchanged" and freezes the memo. Wrapping it in a ref keeps the
 * dependency list itself a fixed length of one.
 */
function useStableArray<T>(next: T[]): T[] {
  const ref = useRef(next);
  const changed =
    ref.current.length !== next.length ||
    next.some((value, index) => value !== ref.current[index]);
  if (changed) {
    ref.current = next;
  }
  return ref.current;
}

function dedupeAssets(assets: ConnectedAsset[]): ConnectedAsset[] {
  const seen = new Set<string>();
  const deduped: ConnectedAsset[] = [];
  for (const asset of assets) {
    const key = `${asset.type}:${asset.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(asset);
  }
  return deduped;
}

/**
 * Bulk variant of useTableConnectedAssets: aggregates connected assets
 * across every selected table id, deduped by type+id (the same connection
 * can be shared by more than one selected table). No batch BFF endpoint
 * exists yet, so this fans out the existing single-table procedures via
 * trpc.useQueries -- one call per connected-asset type, each mapping over
 * every table id. An empty tableIds array fires zero queries.
 */
export function useTablesConnectedAssets(tableIds: string[]) {
  const canvasesQueries = trpc.useQueries((t) =>
    tableIds.map((tableId) => t.table.connectedCanvases({ tableId })),
  );
  const interfacesQueries = trpc.useQueries((t) =>
    tableIds.map((tableId) => t.table.connectedInterfaces({ tableId })),
  );
  const chatbotsQueries = trpc.useQueries((t) =>
    tableIds.map((tableId) => t.table.connectedChatbots({ tableId })),
  );
  const zapsQueries = trpc.useQueries((t) =>
    tableIds.map((tableId) => t.table.connectedZaps({ tableId })),
  );
  const subscriptionsQueries = trpc.useQueries((t) =>
    tableIds.map((tableId) => t.table.connectedSubscriptions({ tableId })),
  );
  const viewsQueries = trpc.useQueries((t) =>
    tableIds.map((tableId) => t.table.connectedViews({ tableId })),
  );

  // trpc.useQueries returns a new array wrapper on every render even when
  // the underlying results haven't changed. Depending on each query's own
  // `.data` -- stable across renders when unchanged -- instead of the
  // wrapper array keeps these memos (and transformedAssets below) from
  // producing a new reference on every render. useStableArray keeps each
  // dependency list a fixed length of one so a growing/shrinking selection
  // doesn't defeat React's deps comparison.
  const canvasesData = useStableArray(
    canvasesQueries.map((query) => query.data),
  );
  const interfacesData = useStableArray(
    interfacesQueries.map((query) => query.data),
  );
  const chatbotsData = useStableArray(
    chatbotsQueries.map((query) => query.data),
  );
  const zapsData = useStableArray(zapsQueries.map((query) => query.data));
  const subscriptionsData = useStableArray(
    subscriptionsQueries.map((query) => query.data),
  );
  const viewsData = useStableArray(viewsQueries.map((query) => query.data));

  const transformedCanvases = useMemo(
    () =>
      canvasesData.flatMap((data) =>
        transformCanvasConnections(data?.tableConnectedCanvases),
      ),
    [canvasesData],
  );

  const transformedInterfaces = useMemo(
    () =>
      interfacesData.flatMap((data) =>
        transformInterfaceConnections(data?.tableConnectedInterfaces),
      ),
    [interfacesData],
  );

  const transformedChatbots = useMemo(
    () =>
      chatbotsData.flatMap((data) =>
        transformChatbotConnections(data?.tableConnectedChatbots),
      ),
    [chatbotsData],
  );

  const transformedZaps = useMemo(
    () =>
      zapsData.flatMap((data) =>
        transformZapConnections(data?.tableConnectedZaps),
      ),
    [zapsData],
  );

  const transformedSubscriptions = useMemo(
    () =>
      subscriptionsData.flatMap((data) =>
        transformSubscriptionConnections(data?.tableSubscriptions),
      ),
    [subscriptionsData],
  );

  const transformedTableViews = useMemo(
    () =>
      viewsData.flatMap((data) =>
        transformTableViewConnections(data?.tableViews),
      ),
    [viewsData],
  );

  const transformedAssets = useMemo(
    () =>
      dedupeAssets([
        ...transformedCanvases,
        ...transformedInterfaces,
        ...transformedChatbots,
        ...transformedZaps,
        ...transformedSubscriptions,
        ...transformedTableViews,
      ]),
    [
      transformedCanvases,
      transformedInterfaces,
      transformedChatbots,
      transformedZaps,
      transformedSubscriptions,
      transformedTableViews,
    ],
  );

  const hasNonBlockingConnections =
    transformedCanvases.length > 0 ||
    transformedChatbots.length > 0 ||
    transformedZaps.length > 0 ||
    transformedSubscriptions.length > 0;

  const hasBlockingConnections = transformedInterfaces.length > 0;

  const allQueries = [
    ...canvasesQueries,
    ...interfacesQueries,
    ...chatbotsQueries,
    ...zapsQueries,
    ...subscriptionsQueries,
    ...viewsQueries,
  ];

  return {
    hasNonBlockingConnections,
    hasBlockingConnections,
    hasSubscriptions: transformedSubscriptions.length > 0,
    hasViews: transformedTableViews.length > 0,
    viewCount: transformedTableViews.length,
    transformedAssets,
    isLoading: allQueries.some((query) => query.isLoading),
    isFetching: allQueries.some((query) => query.isFetching),
  };
}
